import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

import type { ScopeKey } from '../../domain/access';
import type { DateRange, ScopeFilters } from '../../types/extranet-api.types';

/**
 * Début de la période par défaut du header : **la semaine en cours**, depuis le
 * lundi à 00:00 UTC (retour d'Alexandre, 22 septembre 2026 — c'était les 30
 * derniers jours).
 *
 * En UTC comme les autres préréglages et comme `formatRange` : un lundi calculé
 * en heure locale tomberait le dimanche à 22 h UTC, et le libellé de la période
 * afficherait alors la veille. Contrepartie assumée : le lundi entre minuit et
 * 2 h (heure de Paris), la « semaine en cours » est encore la précédente.
 */
export const defaultRangeStartISO = () => {
  const d = new Date();
  const daysSinceMonday = (d.getUTCDay() + 6) % 7; // getUTCDay : 0 = dimanche
  d.setUTCDate(d.getUTCDate() - daysSinceMonday);
  d.setUTCHours(0, 0, 0, 0);
  return d.toISOString();
};

const now = () => new Date().toISOString();

export interface GlobalFiltersState {
  /** Valeurs de périmètre par dimension (`billingReference1`, `costCenter`…). */
  scopes: ScopeFilters;
  dateRange: DateRange;
  setScope: (key: ScopeKey, values: string[]) => void;
  setDateRange: (range: DateRange) => void;
  reset: () => void;
}

const initialState = {
  scopes: {} as ScopeFilters,
  dateRange: { from: defaultRangeStartISO(), to: now() },
};

/** Nombre de valeurs de périmètre actives, toutes dimensions confondues. */
export const countScopeValues = (scopes: ScopeFilters): number =>
  Object.values(scopes).reduce((sum, values) => sum + (values?.length ?? 0), 0);

/**
 * Version 2 : les anciens `entityIds` / `contractIds` / `siteIds` (routes
 * `/v1/extranet/scope/*`, jamais livrées) sont remplacés par les cinq filtres
 * communs du contrat v1.8.4.
 *
 * Version 3 : la période par défaut passe du mois courant aux 30 derniers jours.
 * Version 4 : elle passe à la semaine en cours.
 * Sans ces bumps, une session déjà ouverte garderait l'ancien défaut persisté et
 * ne verrait jamais le nouveau.
 *
 * Un état persisté d'une version antérieure est jeté.
 */
const STORE_VERSION = 4;

export const useFilterStore = create<GlobalFiltersState>()(
  persist(
    set => ({
      ...initialState,
      setScope: (key, values) =>
        set(state => {
          const scopes = { ...state.scopes };
          if (values.length > 0) scopes[key] = values;
          else delete scopes[key];
          return { scopes };
        }),
      setDateRange: range => set({ dateRange: range }),
      reset: () => set(initialState),
    }),
    {
      name: 'extranet:filters',
      version: STORE_VERSION,
      storage: createJSONStorage(() => sessionStorage),
      migrate: (persisted, version) => (version < STORE_VERSION ? initialState : (persisted as GlobalFiltersState)),
    },
  ),
);
